import React from 'react';
import { useSearchParams } from 'react-router-dom';
import TabBar from '../../filtering/TabBar';
import WaiverRequirementsSettings from '../WaiverRequirementsSettings';
import WaiverSubmissionsSettings from '../WaiverSubmissionsSettings';
import { LoadedUser, TabInt } from '../../../interfaces';
import { getWaiverManagementPermission } from '../../../helpers/waivers';
import useUser from '../../../hooks/global/useUser';

const TabsData = {
  requirements: {
    id: 'requirements',
    label: 'Requirements & Documents',
    component: <WaiverRequirementsSettings />,
    order: 0,
  },
  submissions: {
    id: 'submissions',
    label: 'Submissions',
    component: <WaiverSubmissionsSettings />,
    order: 1,
  },
} as const;

interface Tab extends TabInt {
  id: (typeof TabsData)[keyof typeof TabsData]['id'];
}

const DEFAULT_INDEX = 0;

function WaiversTabs() {
  const { user } = useUser<LoadedUser>();
  const [params, setParams] = useSearchParams();

  const canManageWaivers = getWaiverManagementPermission(user);

  const tabs = React.useMemo(() => {
    const tempTabs: Tab[] = [
      {
        id: TabsData.submissions.id,
        label: TabsData.submissions.label,
        component: TabsData.submissions.component,
      },
    ];
    if (canManageWaivers)
      tempTabs.unshift({
        id: TabsData.requirements.id,
        label: TabsData.requirements.label,
        component: TabsData.requirements.component,
      });
    return tempTabs;
  }, [canManageWaivers]);

  const getInitialTabIndex = (tab: string | null) => {
    if (!tab) {
      return DEFAULT_INDEX;
    }
    const index = tabs.findIndex(t => t.id === tab);
    return index === -1 ? DEFAULT_INDEX : index;
  };

  const initialTab = params.get('tab');
  const initialTabIndex = getInitialTabIndex(initialTab);

  const [selectedTabIndex, setSelectedTabIndex] =
    React.useState(initialTabIndex);

  const onChangeTab = (index: number) => {
    setSelectedTabIndex(index);
    setParams(prev => {
      if (index === DEFAULT_INDEX) {
        prev.delete('tab');
      } else {
        prev.set('tab', tabs[index].id);
      }
      return prev.toString();
    });
  };

  return (
    <TabBar
      changeTab={onChangeTab}
      currentTab={selectedTabIndex}
      tabs={tabs}
      noTopMargin
    />
  );
}

export default WaiversTabs;
